const pendingHomeworkSaves = new Set()

// При переходе в журнал/расписание GET не должен обогнать сохранение закрываемого редактора.
export const waitForHomeworkSaves = () => Promise.all(
  Array.from(pendingHomeworkSaves, queue => queue.flush()),
)

// Одна последовательная очередь на строку, живёт и после закрытия редактора.
export const createHomeworkAutosave = ({ initial, draftKey, storage, save, delay = 800 }) => {
  let snapshot = initial
  let version = initial.homework_version ?? 0
  let dirty = false
  let status = 'saved'
  let error = ''
  let timer
  let running
  let revision = 0
  let generation = 0
  let storedDraft
  const newHistoryKey = () => `homework:${Date.now()}:${Math.random().toString(36).slice(2)}`
  let historyKey = newHistoryKey()
  const listeners = new Set()
  const notify = () => listeners.forEach(listener => listener())
  const persist = () => {
    try {
      const draft = JSON.stringify({ ...snapshot, homework_version: version })
      storage.setItem(draftKey, draft)
      storedDraft = draft
      return true
    } catch {
      return false
    }
  }
  try {
    storedDraft = storage.getItem(draftKey)
    const draft = JSON.parse(storedDraft)
    if (draft?.homework_rich) {
      snapshot = { ...draft, history_key: historyKey }
      version = draft.homework_version ?? 0
      dirty = true
    }
  } catch { /* Недоступное хранилище не должно блокировать HTTP-сохранение. */ }

  const flush = () => {
    clearTimeout(timer)
    if (running) return running
    if (!dirty) return Promise.resolve()
    pendingHomeworkSaves.add(queue)
    running = (async () => {
      while (dirty) {
        const sentRevision = revision
        const sent = snapshot
        status = 'saving'
        error = ''
        notify()
        try {
          const saved = await save({ ...sent, homework_version: version })
          version = saved.homework_version
          dirty = revision !== sentRevision
          if (dirty) {
            persist()
          } else {
            snapshot = saved
            try {
              // Ответ старого окна не должен удалять более новый draft другого окна.
              if (storage.getItem(draftKey) === storedDraft) storage.removeItem(draftKey)
            } catch { /* Повторная отправка защищена версией. */ }
          }
          if (!dirty) pendingHomeworkSaves.delete(queue)
          status = dirty ? 'saving' : 'saved'
          notify()
        } catch (e) {
          pendingHomeworkSaves.delete(queue)
          status = 'error'
          error = e?.response?.status === 409
            ? 'ДЗ изменено в другом окне. Ваш черновик сохранён; сравните версии перед повторной записью.'
            : 'Не удалось сохранить ДЗ. Повторите попытку.'
          persist()
          notify()
          break
        }
      }
    })().finally(() => { clearTimeout(timer); running = null })
    return running
  }

  const queue = {
    canRelease: () => listeners.size === 0,
    read: () => ({ snapshot, status, error, dirty, generation }),
    acceptServer: next => {
      if (dirty || running || (next.homework_version ?? 0) <= version) return
      snapshot = next
      version = next.homework_version
      generation += 1
      notify()
    },
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener) },
    change: patch => {
      snapshot = { ...snapshot, ...patch, history_key: historyKey }
      revision += 1
      dirty = true
      pendingHomeworkSaves.add(queue)
      error = persist() ? '' : 'Локальный черновик недоступен. Не закрывайте страницу до сохранения.'
      status = 'saving'
      notify()
      clearTimeout(timer)
      timer = setTimeout(flush, delay)
    },
    flush,
    finish: () => {
      const pending = flush()
      historyKey = newHistoryKey()
      return pending
    },
    // Только явное решение пользователя после просмотра серверной версии.
    rebase: serverVersion => { version = serverVersion; persist(); return flush() },
  }
  if (dirty) pendingHomeworkSaves.add(queue)
  return queue
}
